/**
 * APPLICATION ENTRY POINT
 *
 * This is the first file that runs when the app loads. It:
 * 1. Imports React and the main App component
 * 2. Initializes Sentry for error monitoring
 * 3. Finds the root HTML element (div with id="root")
 * 4. Renders the App inside React's StrictMode
 *
 * StrictMode helps catch potential bugs during development by:
 * - Detecting unexpected side effects
 * - Warning about deprecated APIs
 * - Ensuring components are resilient to being mounted/unmounted
 *
 * Note: StrictMode only runs in development, not production.
 */

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App.tsx'

// Check if we're in production (works with Vite)
const isProduction = import.meta.env.PROD || window.location.hostname !== 'localhost';

// Initialize Sentry for error monitoring
Sentry.init({
  dsn: "https://1530de5278ee5101c0575bcb268dceed@o4510792213397504.ingest.us.sentry.io/4510792234565632",

  // Only send errors in production
  enabled: isProduction,

  // Sample rate for performance monitoring (1.0 = 100%)
  tracesSampleRate: 1.0,

  // Set environment
  environment: isProduction ? 'production' : 'development',

  // Integrate with React for better error boundaries
  integrations: [
    Sentry.browserTracingIntegration(),
  ],

  // Filter out known-benign browser noise before it hits the dashboard.
  // Each entry here is an error we've decided isn't actionable:
  //   - FCM "browser doesn't support" — Safari/iOS/private tabs lack the
  //     Push API; we've already gated FCM registration on runtime capability.
  //   - AbortError — fired when the user navigates away mid-fetch. Harmless.
  //   - InvalidStateError on IDBDatabase — private tabs blocking IndexedDB.
  //     Firebase auth falls back gracefully.
  // If any of these ever masks a real bug, remove from ignoreErrors and
  // investigate. Add new patterns with the same rationale documented.
  ignoreErrors: [
    /Messaging: This browser doesn't support/i,
    /AbortError: The operation was aborted/i,
    /InvalidStateError.*IDBDatabase/i,
  ],
});

// Log to confirm Sentry status (remove after testing)
console.log('Sentry enabled:', isProduction);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* Sentry.ErrorBoundary catches React render errors so users see a
        friendly recover screen instead of a white page. Fallback below
        matches the app's glass palette. The error is auto-captured to
        Sentry with a component-stack breadcrumb, so it appears in the
        dashboard with more context than a plain crash. */}
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            textAlign: 'center',
            color: '#e2e8f0',
            background:
              'linear-gradient(135deg, #4a235a 0%, #6c3483 50%, #9b59b6 100%)',
          }}
        >
          <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
            Something went wrong.
          </h1>
          <p style={{ maxWidth: 480, marginBottom: '1.5rem', opacity: 0.85 }}>
            We hit an unexpected error. It's been reported so we can fix it.
            Try again, or come back in a bit.
          </p>
          <button
            onClick={resetError}
            style={{
              padding: '0.75rem 1.5rem',
              borderRadius: 9999,
              border: 'none',
              background: '#9b59b6',
              color: '#fff',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      )}
    >
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
